/**
 * /ja/comparisons/notion-ai — Draftlize vs Notion (JA, single-locale).
 * Mirrors the EN NotionAi angle: keep Notion; Draftlize is the substrate
 * under the AI-assisted parts. Links to /ja/decision-log.
 */
import { MarketingShell } from '../MarketingShell';

export function NotionAiJa() {
  return (
    <MarketingShell
      currentLocale="ja"
      hreflangPath="/comparisons/notion-ai"
      hreflangLocales={['ja']}
      documentTitle="プロダクト業務のNotion代替｜Draftlize vs Notion · Draftlize"
      documentDescription="Notionはページを保存する。Draftlizeは決定を「生きた状態」でつなぐカードグラフ——AIエージェントが読み書きできる。両者を並べて比較します。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">比較 · vs Notion</span>
          <h1 className="serif" style={{ fontSize: 'clamp(36px, 4.8vw, 60px)', lineHeight: 1.02, margin: '12px 0 24px' }}>
            Draftlize vs Notion——<br />
            <em>プロダクト業務にどっち？</em>
          </h1>
          <p className="lede">
            結論から言うと、Notion はページに書き込むための最高のツールです。Draftlize は、AI エージェントが<em>読み書きできる</em>構造化カードとして製品の思考を蓄える基盤です。両者は隣り合う問題を解きます——片方でもう片方を置き換えるというより、いま必要なのはどちらかを見極める話です。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">要点</span>
            <h2 className="serif">並べて比較、<em>7つの観点。</em></h2>
          </div>
          <p>同じロードマップ・同じシナリオを両方のツールで走らせた結果から。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>Notion</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>基盤の形</td><td className="ours yes">型付きカードグラフ</td><td className="partial">ブロックのページ</td></tr>
            <tr><td>AI が決定を毎回の文脈として読む</td><td className="ours yes">ネイティブ</td><td className="no">—</td></tr>
            <tr><td>AI が構造化カードとして書き戻す</td><td className="ours yes">ネイティブ</td><td className="partial">ブロックは書けるが構造なし</td></tr>
            <tr><td>依存が変わると stale 伝播</td><td className="ours yes">ネイティブ</td><td className="no">—</td></tr>
            <tr><td>自由記述のドキュメント・DB</td><td className="no">—</td><td className="yes">優秀</td></tr>
            <tr><td>チーム wiki・権限・共同編集</td><td className="no">個人向け（v1）</td><td className="yes">優秀</td></tr>
            <tr><td>Claude Code / MCP 連携</td><td className="ours yes">ネイティブ</td><td className="no">—</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif"><em>Notion が勝つ</em>ところ。</h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">wiki が必要なら</h3>
            <p>Notion は現存する最高の wiki ツールです。「チームの手順書をどこに置くか」が課題なら Notion。Draftlize は wiki を目指していません。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">テーブルと DB で生きているなら</h3>
            <p>Notion のデータベース／ビューは無敵です。ロードマップ表、スプリント管理、比較表——こうした形は Notion の構造化コレクションが常に上です。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">チームが大きいなら</h3>
            <p>リアルタイム共同編集、細かな権限、コメント、メンション。Notion はそのために作られています。Draftlize は個人先行の設計です。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif"><em>Draftlize が勝つ</em>ところ。</h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">本当に「覚えている」AI</h3>
            <p>Notion AI の記憶は、チャットへのベストエフォートな検索です。Draftlize では、エージェントが作業する基盤そのものがカードなので、価格の決定カードを毎回自動で読みます。説明のやり直しがゼロに。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">spec drift が可視になる</h3>
            <p>Notion のページを変えても誰も気づかない。Draftlize では決定カードを変えると、依存するカードが自動で <code>stale</code> に。ビルドシステムが無効化を追うように、基盤が変更を追跡します。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Claude Code / Cursor / MCP の一等コンテキスト</h3>
            <p>Notion AI は Notion の中だけ。Draftlize のカードは CLI で repo に落ち、MCP 経由で各 AI ツールから参照できます。決定はどのツールからも指し示せます。詳しくは <a href="/ja/decision-log" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>意思決定ログ</a>。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Notion は「チームがどこに書き留めるか」の答え。Draftlize は「AI が仕事の半分をするとき、製品の思考がどこで積み上がるか」の答え。
        </blockquote>
        <cite>前者だけで十分なら、Notion で足ります。</cite>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">$5 無料で<em>始める。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新規登録で $5 付与</span>
          <span>使った分だけ支払い</span>
          <span>残高は無期限</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Notion はそのままでいい。Draftlize は、AI が関わる部分の下に敷く基盤です。新規登録で $5、使った分だけ、残高は無期限。
          </p>
          <a className="btn-primary" href="/">$5 無料で始める</a>
        </div>
      </section>
    </MarketingShell>
  );
}
